import { SelectOptionsByFieldType } from '@monorepo/shared/apiClient/logs/fieldOptionsForUpload';
import {
  FieldDataType,
  FieldResponse,
  FieldValueResponse,
  ResourceFieldResponse,
  SaveFieldValueRequest,
  SaveLogEntryRequest,
} from 'mapistry-shared';

function convertSaveFieldValueToFieldValueResponse({
  logFieldValue,
  logField,
  logFieldOptions,
  entry,
}: {
  logFieldValue: SaveFieldValueRequest;
  logField: FieldResponse;
  logFieldOptions: SelectOptionsByFieldType;
  entry: SaveLogEntryRequest;
}): FieldValueResponse {
  const res = {
    id: logFieldValue.fieldId,
    entryId: entry.id || '',
    fieldId: logFieldValue.fieldId,
    fieldType: logField.type,
  };

  if (logFieldValue.valueVersions) {
    throw new Error('Field value versions are not supported for logs.');
  }

  switch (logField.type) {
    case FieldDataType.BOOLEAN:
    case FieldDataType.SINGLE_SELECT:
    case FieldDataType.TEXT:
    case FieldDataType.TIME:
    case FieldDataType.DATE:
    case FieldDataType.DATETIME:
    case FieldDataType.NUMERIC:
      return {
        ...res,
        fieldValue: logFieldValue.fieldValue,
      };
    case FieldDataType.RESOURCE:
      return {
        ...res,
        fieldValue: {
          value: logFieldValue.fieldValue.value
            ? {
                id: logFieldValue.fieldValue.value,
                name: logFieldOptions?.resources?.[
                  (logField as ResourceFieldResponse).resourceTypeId
                ]?.find((r) => r.value === logFieldValue.fieldValue.value)
                  ?.label,
              }
            : null,
        },
      } as FieldValueResponse;
    case FieldDataType.USER:
      return {
        ...res,
        fieldValue: {
          value: logFieldValue.fieldValue.value
            ? {
                id: logFieldValue.fieldValue.value,
                name: logFieldOptions?.users?.find(
                  (u) => u.value === logFieldValue.fieldValue.value,
                )?.label,
              }
            : null,
        },
      } as FieldValueResponse;
    default: {
      const exhaustiveCheck: never | FieldDataType.FORMULA = logField.type;
      throw new Error(`Unhandled field data type - ${exhaustiveCheck}`);
    }
  }
}

export function convertSaveFieldValuesToFieldValueResponses({
  logFields,
  logFieldOptions,
  entry,
}: {
  logFields: FieldResponse[];
  logFieldOptions: SelectOptionsByFieldType;
  entry: SaveLogEntryRequest;
}): Record<FieldResponse['id'], FieldValueResponse> {
  return entry.logFieldValues.reduce((acc, logFieldValue) => {
    const logField = logFields.find((f) => f.id === logFieldValue.fieldId);
    if (!logField) throw new Error('Log field not found.');
    return {
      ...acc,
      [logField.id]: convertSaveFieldValueToFieldValueResponse({
        logFieldValue,
        logField,
        logFieldOptions,
        entry,
      }),
    };
  }, {} as Record<FieldResponse['id'], FieldValueResponse>);
}
